import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../auth/AuthContext";

export const Navbar = ({ appName, menuItems = [], hideUserName = false }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const activeLinkClass = "text-slate-900 border-b-2 border-slate-800";
  const textColorClass = "text-slate-600";
  const hoverColorClass = "hover:text-slate-900 transition-colors";
  const navLogo = "/favicon.svg";

  const isActive = (path) => {
    if (!path) return false;
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.toLowerCase().startsWith(path.toLowerCase())) return true;
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <nav className="w-full fixed top-0 left-0 z-50 py-3 bg-white/95 border-b border-slate-200 transition-all duration-300 backdrop-blur-sm">
        <div className="container mx-auto px-4 flex items-center justify-between" ref={menuRef}>
          {/* Left: Logo & App Name */}
          <div className="flex-1 flex items-center min-w-0">
            <Link
              to="/"
              className="flex items-center space-x-2 md:space-x-3 text-2xl font-bold overflow-hidden"
              onClick={closeMenu}
            >
              <motion.img
                src={navLogo}
                alt="Logo"
                className="w-10 h-10 shrink-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
              <span className="text-[12px] md:text-xl font-bold text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis uppercase">
                VIONIXASIA
              </span>
            </Link>
          </div>

          {/* Desktop Menu Items */}
          <div className="hidden md:flex items-center space-x-8 text-sm uppercase font-semibold">
            {menuItems.map((item, idx) => (
              item.to ? (
                <Link
                  key={item.to || idx}
                  to={item.to}
                  className={`${
                    isActive(item.to) ? activeLinkClass : textColorClass
                  } ${hoverColorClass} pb-1`}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={idx}
                  onClick={() => {
                    item.onClick?.();
                    closeMenu();
                  }}
                  className={`${textColorClass} ${hoverColorClass} pb-1 uppercase font-semibold text-sm`}
                >
                  {item.label}
                </button>
              )
            ))}

            {/* User Profile / Login */}
            {!hideUserName && user && (
                <Link
                  to="/profile"
                  className={`${
                    isActive("/profile") ? activeLinkClass : textColorClass
                  } ${hoverColorClass} pb-1 flex items-center gap-2`}
                >
                  {user.displayName || user.email?.split("@")[0] || "Profile"}
                </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden z-50 relative focus:outline-none p-2"
          >
            <svg
              className="w-7 h-7 text-slate-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Fullscreen Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-white z-40 flex flex-col md:hidden pt-20 overflow-y-auto"
          >
            <div className="flex flex-col w-full px-6 uppercase font-bold divide-y divide-slate-100">
              <Link
                to="/"
                className={`py-4 text-base tracking-wide ${
                  isActive("/") ? "text-slate-900" : "text-slate-500"
                }`}
                onClick={closeMenu}
              >
                Home
              </Link>
              
              {menuItems.map((item, idx) => (
                item.to ? (
                  <Link
                    key={item.to || idx}
                    to={item.to}
                    className={`py-4 text-base tracking-wide ${
                      isActive(item.to) ? "text-slate-900" : "text-slate-500"
                    }`}
                    onClick={closeMenu}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={idx}
                    onClick={() => {
                      item.onClick?.();
                      closeMenu();
                    }}
                    className="py-4 text-base tracking-wide text-left text-slate-500 hover:text-slate-900 uppercase font-bold"
                  >
                    {item.label}
                  </button>
                )
              ))}

              {!hideUserName && user && (
                <>
                  <Link
                    to="/profile"
                    className={`py-4 text-base tracking-wide break-words ${
                      isActive("/profile") ? "text-slate-900" : "text-slate-500"
                    }`}
                    onClick={closeMenu}
                  >
                    {user.displayName || "Profile"}
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
